import { useState, useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { AuthProvider, useAuth } from "@/hooks/useAuth";

import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "@/components/ErrorFallback";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
import { analytics } from "@/lib/analytics";
import { purgeExpiredSessionState } from "@/hooks/useSessionState";

// Route-level code splitting: the landing route is the only chunk parsed on
// first paint. Everything else (charts, PDF export, planner, VIP) downloads on
// demand, which is the difference between a usable and an unusable phone.
import Index from "./pages/Index";
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const SharedCollection = lazy(() => import("./pages/SharedCollection"));
const Community = lazy(() => import("./pages/Community"));
const MovieNight = lazy(() => import("./pages/MovieNight"));
const Collections = lazy(() => import("./pages/Collections"));
const CollectionDetail = lazy(() => import("./pages/CollectionDetail"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const Messages = lazy(() => import("./pages/Messages"));
const Admin = lazy(() => import("./pages/Admin"));
const WatchPlanner = lazy(() => import("./pages/WatchPlanner"));
const VIP = lazy(() => import("./pages/VIP"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));
const EmailUnsubscribe = lazy(() => import("./pages/EmailUnsubscribe"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Support = lazy(() => import("./pages/Support"));

// Onboarding is never needed on first paint.
const OpeningNight = lazy(() => import("@/components/onboarding/OpeningNight"));
const CinemaFX = lazy(() =>
  import("@/components/CinemaFX").then((m) => ({ default: m.CinemaFX }))
);
const SpotlightCursor = lazy(() =>
  import("@/components/SpotlightCursor").then((m) => ({ default: m.SpotlightCursor }))
);
const ManualSearchWindow = lazy(() =>
  import("@/components/search/ManualSearchWindow").then((m) => ({ default: m.ManualSearchWindow }))
);

import {
  Users,
  PartyPopper,
  MessageSquare,
  Bell,
  CalendarClock,
  ShieldCheck,
  Film,
  LifeBuoy,
} from "lucide-react";
import { useBanGuard } from "@/hooks/useBanGuard";
import { SuspensionScreen } from "@/components/SuspensionScreen";
import { MarqueeDock } from "@/components/nav/MarqueeDock";
import { SeatPrompt } from "@/components/guest/SeatPrompt";
import { CookieConsent } from "@/components/CookieConsent";
import { MemberRoute } from "@/components/guest/MemberRoute";




const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Initialize analytics once
analytics.init();

// Purge expired persisted state on boot (4-hour TTL)
purgeExpiredSessionState();

/**
 * Mounts decorative/secondary chunks only once the browser is idle, so they
 * never compete with the first paint on a low-end phone.
 */
const useIdleMount = (delay = 1200) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => setReady(true), { timeout: delay + 1500 });
      return () => (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(id);
    }
    const t = setTimeout(() => setReady(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return ready;
};

// Decorative cursor beam + cinema FX overlays — deferred, never blocking paint.
const DeferredAmbience = () => {
  const ready = useIdleMount();
  const { pathname } = useLocation();
  if (!ready) return null;
  const showSpotlight = pathname === "/" || pathname === "/vip" || pathname.startsWith("/movie-night");
  return (
    <Suspense fallback={null}>
      {showSpotlight && <SpotlightCursor />}
      <CinemaFX />
    </Suspense>
  );
};


const PasswordRecoveryRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    if (hashParams.get('type') === 'recovery' && hashParams.get('access_token')) {
      sessionStorage.setItem('swiftix_password_recovery', '1');
      if (window.location.pathname !== '/reset-password') {
        navigate(`/reset-password${window.location.hash}`, { replace: true });
      }
    }
  }, [navigate]);

  return null;
};

const App = () => (
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <PasswordRecoveryRedirect />
            <BanGate />
            <SeatPrompt />
            <CookieConsent />
            <Suspense fallback={null}>
              <ManualSearchWindow />
            </Suspense>
            <DeferredAmbience />

          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);


// Renders the suspension screen for banned users; otherwise renders all routes.
const BanGate = () => {
  const { banned, ready } = useBanGuard();
  if (!ready) return null;
  if (banned) return <SuspensionScreen />;
  return (
    <>
      <MarqueeDock />
      <Suspense fallback={null}>
        <OpeningNight />
      </Suspense>
      <Suspense fallback={<RouteFallback />}>
        <SentryRoutes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/collection/:id" element={<SharedCollection />} />
              <Route path="/collections" element={<Collections />} />
              <Route path="/collections/:id" element={<MemberRoute icon={Film} title="That collection needs a seat" description="Your own collections and the titles inside them live with your account."><CollectionDetail /></MemberRoute>} />
              <Route path="/community" element={<MemberRoute icon={Users} title="The lobby is for ticket holders" description="Follow other moviegoers, join discussions, and share collections once you have a seat."><Community /></MemberRoute>} />
              <Route path="/movie-night" element={<MemberRoute icon={PartyPopper} title="Movie Night needs a seat" description="Invite friends by handle, vote on the slate, and settle the showing together."><MovieNight /></MemberRoute>} />
              <Route path="/messages" element={<MemberRoute icon={MessageSquare} title="Messages are private" description="Direct chats with other moviegoers open up once you claim your ticket."><Messages /></MemberRoute>} />
              <Route path="/messages/:conversationId" element={<MemberRoute icon={MessageSquare} title="Messages are private" description="Direct chats with other moviegoers open up once you claim your ticket."><Messages /></MemberRoute>} />
              <Route path="/u/:userId" element={<PublicProfile />} />
              <Route path="/admin" element={<MemberRoute icon={ShieldCheck} title="Staff only" description="This door leads backstage."><Admin /></MemberRoute>} />
              <Route path="/watch-planner" element={<MemberRoute icon={CalendarClock} title="The program board is yours to fill" description="Build a marathon night by night. One active program is free once you have a seat."><WatchPlanner /></MemberRoute>} />
              <Route path="/vip" element={<VIP />} />
              <Route path="/email-preferences" element={<EmailPreferences />} />
              <Route path="/email-unsubscribe" element={<EmailUnsubscribe />} />
              <Route path="/account-settings" element={<AccountSettings />} />
              <Route path="/notifications" element={<MemberRoute icon={Bell} title="Your notices live behind the counter" description="Invites, follows, and replies are all tied to your own seat."><Notifications /></MemberRoute>} />
              <Route path="/support" element={<MemberRoute icon={LifeBuoy} title="The Box Office needs your ticket" description="Support conversations are tied to your account so we can find your history."><Support /></MemberRoute>} />
              <Route path="/support/:ticketId" element={<MemberRoute icon={LifeBuoy} title="The Box Office needs your ticket" description="Support conversations are tied to your account so we can find your history."><Support /></MemberRoute>} />
              <Route path="*" element={<NotFound />} />
        </SentryRoutes>
      </Suspense>
    </>
  );
};

// Quiet placeholder while a route chunk streams in.
const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" aria-busy="true">
    <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-muted-foreground">
      Taking your seat…
    </span>
  </div>
);


export default App;

